import { useState, useEffect } from "react";
import { useAuth } from "../App";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { UserCircle, Mail, Phone, CreditCard, Shield, Key, LogOut } from "lucide-react";
import { useNavigate } from "react-router-dom";

const PerfilPage = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [editMode, setEditMode] = useState(false);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const getRoleBadgeColor = (role) => {
    const colors = {
      admin: "bg-red-100 text-red-700 border-red-200",
      funcionario: "bg-blue-100 text-blue-700 border-blue-200",
      contribuyente: "bg-green-100 text-green-700 border-green-200",
      auditor: "bg-purple-100 text-purple-700 border-purple-200"
    };
    return colors[role] || colors.contribuyente;
  };

  const getRoleLabel = (role) => {
    const labels = {
      admin: "Administrador",
      funcionario: "Funcionario",
      contribuyente: "Contribuyente",
      auditor: "Auditor"
    };
    return labels[role] || role;
  };

  return (
    <div className="space-y-6" data-testid="perfil-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <div className="w-12 h-12 bg-teal-100 rounded-xl flex items-center justify-center">
          <UserCircle className="w-6 h-6 text-teal-600" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Perfil de Usuario
          </h1>
          <p className="text-slate-500">Gestione su información personal</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Profile Card */}
        <Card className="lg:col-span-1" data-testid="profile-card">
          <CardContent className="p-6 text-center">
            <div className="w-24 h-24 bg-blue-800 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-white text-3xl font-bold">
                {user?.nombre?.charAt(0)}{user?.apellido?.charAt(0)}
              </span>
            </div>
            <h2 className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {user?.nombre} {user?.apellido}
            </h2>
            <Badge variant="outline" className={`mt-2 ${getRoleBadgeColor(user?.role)}`}>
              <Shield className="w-3 h-3 mr-1" />
              {getRoleLabel(user?.role)}
            </Badge>
            <div className="mt-6 space-y-3 text-left">
              <div className="flex items-center gap-3 text-sm text-slate-600">
                <Mail className="w-4 h-4 text-slate-400" />
                {user?.email}
              </div>
              <div className="flex items-center gap-3 text-sm text-slate-600">
                <CreditCard className="w-4 h-4 text-slate-400" />
                {user?.cedula}
              </div>
              {user?.telefono && (
                <div className="flex items-center gap-3 text-sm text-slate-600">
                  <Phone className="w-4 h-4 text-slate-400" />
                  {user?.telefono}
                </div>
              )}
            </div>
            <Button 
              variant="outline" 
              className="w-full mt-6 text-red-600 border-red-200 hover:bg-red-50"
              onClick={handleLogout}
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Cerrar Sesión
            </Button>
          </CardContent>
        </Card>

        {/* Details Card */}
        <Card className="lg:col-span-2" data-testid="details-card">
          <CardHeader>
            <CardTitle>Información Personal</CardTitle>
            <CardDescription>Actualice sus datos de contacto</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Nombre</Label>
                <Input value={user?.nombre || ""} disabled={!editMode} data-testid="input-nombre" />
              </div>
              <div className="space-y-2">
                <Label>Apellido</Label>
                <Input value={user?.apellido || ""} disabled={!editMode} data-testid="input-apellido" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Correo Electrónico</Label>
              <Input value={user?.email || ""} disabled data-testid="input-email" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Cédula de Identidad</Label>
                <Input value={user?.cedula || ""} disabled data-testid="input-cedula" />
              </div>
              <div className="space-y-2">
                <Label>Teléfono</Label>
                <Input value={user?.telefono || ""} disabled={!editMode} placeholder="No registrado" data-testid="input-telefono" />
              </div>
            </div>
            <div className="flex gap-3 pt-4">
              <Button 
                variant={editMode ? "default" : "outline"}
                onClick={() => setEditMode(!editMode)}
                className={editMode ? "bg-blue-800 hover:bg-blue-900" : ""}
                data-testid="edit-btn"
              >
                {editMode ? "Guardar Cambios" : "Editar Perfil"}
              </Button>
              {editMode && (
                <Button variant="outline" onClick={() => setEditMode(false)} data-testid="cancel-btn">
                  Cancelar
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Security Card */}
        <Card className="lg:col-span-3" data-testid="security-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Key className="w-5 h-5" />
              Seguridad
            </CardTitle>
            <CardDescription>Gestione la seguridad de su cuenta</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="bg-slate-50 rounded-lg p-6 flex items-center justify-between">
              <div>
                <h4 className="font-medium text-slate-900">Seguridad de la Cuenta</h4>
                <p className="text-sm text-slate-500 mt-1">
                  Cambie su contraseña, active MFA, gestione sesiones y vea su actividad reciente.
                </p>
              </div>
              <Button
                onClick={() => navigate("/perfil/seguridad")}
                className="bg-blue-600 hover:bg-blue-700 text-white"
                data-testid="btn-ir-seguridad"
              >
                Gestionar Seguridad
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PerfilPage;
